import { useTranslation } from 'react-i18next'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    ChevronDown,
    GalleryVerticalEnd,
    ListChecks,
    Search,
    Check,
    Download,
    Percent,
    Activity,
    CheckCircle,
    Users,
} from 'lucide-react'
import { CardTableViewToggle } from '@/components/ui/card-table-view-toggle'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import type { MobilisationTeam, MobilisationTaskRow } from '../../api'
import { useTaskActionsBySet, useSaveTaskActionBySet } from '../../hooks'
import { formatDateTime } from '@/lib/dateUtils'

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed'] as const

/** Renders the task-actions list icon only when the task has at least one recorded action. */
function TaskActionsIconButton({
    setId,
    recoveryMobilisationId,
    onClick,
    title,
}: {
    setId: number | null
    recoveryMobilisationId: number
    onClick: () => void
    title: string
}) {
    const { data: actions = [] } = useTaskActionsBySet(setId, recoveryMobilisationId)
    if (actions.length === 0) return null
    return (
        <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={onClick}
            title={title}
        >
            <GalleryVerticalEnd className="h-4 w-4" />
        </Button>
    )
}
function statusToApi(s: string): string {
    if (s === 'Pending') return 'No'
    if (s === 'In Progress') return 'In Progress'
    if (s === 'Completed') return 'Completed'
    return 'No'
}
function statusFromApi(s: string): string {
    const v = (s ?? '').trim()
    if (v === 'No') return 'Pending'
    if (v === 'In Progress' || v === 'InProgress') return 'In Progress'
    if (v === 'Completed' || v === 'Complete' || v === 'complete') return 'Completed'
    return 'Pending'
}

function getMobilisationStatusClass(status: string): string {
    const s = statusFromApi(status)
    if (s === 'In Progress') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
    if (s === 'Completed') return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' // Pending / No
}

function getStatusDotClass(status: string): string {
    const s = statusFromApi(status)
    if (s === 'In Progress') return 'bg-blue-500'
    if (s === 'Completed') return 'bg-green-500'
    return 'bg-slate-400'
}

/** Left border colour for board task cards: pending grey, in progress blue, completed green. */
function getBoardCardBorderClass(status: string): string {
    const s = statusFromApi(status)
    if (s === 'In Progress') return 'border-l-blue-500'
    if (s === 'Completed') return 'border-l-green-500'
    return 'border-l-slate-400'
}

function ProgressRing({ done, total }: { done: number; total: number }) {
    const size = 32
    const stroke = 3
    const r = (size - stroke) / 2
    const circumference = 2 * Math.PI * r
    const pct = total > 0 ? Math.min(1, done / total) : 0
    const offset = circumference - pct * circumference
    return (
        <div className="relative flex h-8 w-8 shrink-0 items-center justify-center">
            <svg width={size} height={size} className="-rotate-90" viewBox={`0 0 ${size} ${size}`}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={stroke}
                    className="text-muted-foreground/30"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={stroke}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    className="text-primary transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center font-mono text-xs font-medium text-foreground">
                {done}/{total}
            </span>
        </div>
    )
}

/** Team status for sidebar dot: not started (grey), in progress (blue), complete (green) */
function getTeamStatusDotClass(done: number, inProgress: number, total: number): string {
    if (total === 0) return 'bg-muted-foreground/50'
    if (done === total) return 'bg-green-500'
    if (inProgress > 0) return 'bg-blue-500'
    return 'bg-muted-foreground/50'
}

/** Shared left sidebar: list of teams with selection, status dot, and progress bar */
function TeamSidebar({
    filteredTeams,
    selectedTeamId,
    onSelectTeam,
    getEditState,
    t,
}: {
    filteredTeams: MobilisationTeam[]
    selectedTeamId: number | null
    onSelectTeam: (teamId: number) => void
    getEditState: (task: MobilisationTaskRow) => { status: string; comments: string }
    t: (key: string, fallback?: string) => string
}) {
    return (
        <div className="w-72 shrink-0 flex flex-col border-r border-border bg-muted/20">
            <div className="shrink-0 px-3 py-2.5 border-b border-border bg-muted/50 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                {t('recoverRequests.mobilisation.teams', 'Teams')} ({filteredTeams.length})
            </div>
            <div className="flex-1 overflow-y-auto py-1">
                {filteredTeams.map((team) => {
                    const tasks = team.tasks ?? []
                    const done = tasks.filter((tk) => getEditState(tk).status === 'Completed').length
                    const inProgress = tasks.filter((tk) => getEditState(tk).status === 'In Progress').length
                    const total = tasks.length
                    const isSelected = selectedTeamId === team.id
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0
                    const dotClass = getTeamStatusDotClass(done, inProgress, total)
                    return (
                        <button
                            key={team.id}
                            type="button"
                            onClick={() => onSelectTeam(team.id)}
                            className={cn(
                                'w-full flex items-start gap-2 px-3 py-2.5 text-left transition-colors border-l-2',
                                isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50 border-transparent'
                            )}
                        >
                            <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', dotClass)} title={total === 0 ? t('recoverRequests.mobilisation.noTasks', 'No tasks') : done === total ? t('recoverRequests.mobilisation.completed', 'Complete') : inProgress > 0 ? t('recoverRequests.mobilisation.inProgress', 'In progress') : t('recoverRequests.mobilisation.notStarted', 'Not started')} />
                            <div className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-foreground">
                                    {team.teamName ?? '—'}
                                </span>
                                <div className="mt-1 flex items-center gap-2">
                                    <div className="h-1 flex-1 min-w-0 overflow-hidden rounded-full bg-muted">
                                        <div
                                            className="h-full rounded-full bg-primary transition-all"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                    <span className="shrink-0 text-xs font-mono text-muted-foreground">
                                        {done}/{total}
                                    </span>
                                </div>
                            </div>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

interface MobilisationBoardViewProps {
    setId: number | null
    selectedTeam: MobilisationTeam | null
    selectedBoardCardId: number | null
    onSelectBoardCard: (id: number | null) => void
    viewMode: 'card' | 'table'
    onViewModeChange: (mode: 'card' | 'table') => void
    getEditState: (task: MobilisationTaskRow) => { status: string; comments: string }
    setTaskEdit: (task: MobilisationTaskRow, field: 'status' | 'comments', value: string) => void
    handleSave: (task: MobilisationTaskRow) => void
    hasUnsavedChanges: (task: MobilisationTaskRow) => boolean
    saveMutation: { isPending: boolean; variables?: { recoveryMobilisationId?: number } }
    canEdit: boolean
    setActionsTaskId: (id: number | null) => void
    getMobilisationStatusClass: (status: string) => string
    getStatusDotClass: (status: string) => string
    statusFromApi: (s: string) => string
    t: (key: string, fallback?: string) => string
}

function BoardTaskCard({
    setId,
    task,
    isSelected,
    onSelect,
    getEditState,
    setTaskEdit,
    handleSave,
    hasUnsavedChanges,
    saveMutation,
    canEdit,
    setActionsTaskId,
    getMobilisationStatusClass,
    getStatusDotClass,
    statusFromApi,
    t,
}: {
    setId: number | null
    task: MobilisationTaskRow
    isSelected?: boolean
    onSelect?: () => void
    getEditState: (task: MobilisationTaskRow) => { status: string; comments: string }
    setTaskEdit: (task: MobilisationTaskRow, field: 'status' | 'comments', value: string) => void
    handleSave: (task: MobilisationTaskRow) => void
    hasUnsavedChanges: (task: MobilisationTaskRow) => boolean
    saveMutation: { isPending: boolean; variables?: { recoveryMobilisationId?: number } }
    canEdit: boolean
    setActionsTaskId: (id: number | null) => void
    getMobilisationStatusClass: (status: string) => string
    getStatusDotClass: (status: string) => string
    statusFromApi: (s: string) => string
    t: (key: string, fallback?: string) => string
}) {
    const state = getEditState(task)
    const taskId = task.recoveryMobilisationId ?? task.id
    const isSaving = saveMutation.isPending && saveMutation.variables?.recoveryMobilisationId === taskId
    const savedStatus = statusFromApi(task.status ?? 'No')
    const borderClass = getBoardCardBorderClass(savedStatus)
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.() } }}
            className={cn(
                'flex flex-col gap-2 rounded-md border border-l-4 border-table-border bg-card p-4 font-sans antialiased text-sm cursor-pointer',
                'transition-[transform,box-shadow] duration-150 ease',
                'hover:-translate-y-0.5 hover:shadow-[0_8px_16px_rgba(0,0,0,0.08)]',
                isSelected ? 'shadow-xl ring-2 ring-primary ring-offset-2 ring-offset-background' : 'shadow-[0_1px_3px_rgba(0,0,0,0.05)]',
                borderClass
            )}
        >
            <div className="font-medium text-foreground">{task.task}</div>
            <div>
                {canEdit ? (
                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button
                                                                type="button"
                                                                className={cn(
                                                                    'inline-flex items-center gap-1.5 rounded-full border-0 px-2.5 py-1 text-xs font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-ring',
                                                                    getMobilisationStatusClass(state.status)
                                                                )}
                                                            >
                                                                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', getStatusDotClass(state.status))} />
                                                                {state.status}
                                                                <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
                                                            </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="start" className="min-w-[180px]">
                                                            {STATUS_OPTIONS.map((opt) => (
                                                                <DropdownMenuItem
                                                                    key={opt}
                                                                    onClick={() => setTaskEdit(task, 'status', opt)}
                                                                >
                                                                    <span className={cn('mr-2 h-2 w-2 rounded-full', getStatusDotClass(opt))} />
                                                                    {opt}
                                                                    {state.status === opt && <Check className="ml-auto h-4 w-4" />}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                ) : (
                                                    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', getMobilisationStatusClass(task.status ?? 'No'))}>
                                                        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', getStatusDotClass(task.status ?? 'No'))} />
                                                        {statusFromApi(task.status ?? 'No')}
                                                    </span>
                                                )}
                                            </div>
                                            {canEdit ? (
                                                <Textarea
                                                    className="min-h-[60px] resize-y text-sm font-sans"
                    value={state.comments}
                    onChange={(e) => setTaskEdit(task, 'comments', e.target.value)}
                    placeholder={t('recoverRequests.mobilisation.commentsPlaceholder', 'Comments')}
                    rows={2}
                />
            ) : (
                <p className="text-sm text-muted-foreground">{state.comments || '—'}</p>
            )}
            {task.completedBy && (
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-medium text-foreground">
                        {(task.completedBy ?? '?').slice(0, 1).toUpperCase()}
                    </span>
                    {task.completedBy}
                </span>
            )}
            {canEdit && (
                <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                    {hasUnsavedChanges(task) && (
                        <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleSave(task)}
                            disabled={isSaving}
                        >
                            {t('common.save', 'Save')}
                        </Button>
                    )}
                    <TaskActionsIconButton
                        setId={setId}
                        recoveryMobilisationId={taskId}
                        onClick={() => setActionsTaskId(taskId)}
                        title={t('recoverRequests.mobilisation.taskActions', 'Task actions')}
                    />
                </div>
            )}
        </div>
    )
}

function MobilisationBoardView({
    setId,
    selectedTeam,
    selectedBoardCardId,
    onSelectBoardCard,
    viewMode,
    onViewModeChange,
    getEditState,
    setTaskEdit,
    handleSave,
    hasUnsavedChanges,
    saveMutation,
    canEdit,
    setActionsTaskId,
    getMobilisationStatusClass,
    getStatusDotClass,
    statusFromApi,
    t,
}: MobilisationBoardViewProps) {
    const statusColumns = useMemo(
        () => [
            { key: 'no' as const, label: t('recoverRequests.mobilisation.pending', 'Pending') },
            { key: 'inProgress' as const, label: t('recoverRequests.mobilisation.inProgress', 'In Progress') },
            { key: 'completed' as const, label: t('recoverRequests.mobilisation.completed', 'Completed') },
        ],
        [t]
    )

    /** Group tasks by saved status (from API) so cards don't move until the user saves. */
    const getTasksByStatus = useCallback(
        (team: MobilisationTeam) => {
            const tasks = team.tasks ?? []
            const no: MobilisationTaskRow[] = []
            const inProgress: MobilisationTaskRow[] = []
            const completed: MobilisationTaskRow[] = []
            tasks.forEach((task) => {
                const s = statusFromApi(task.status ?? 'No')
                if (s === 'In Progress') inProgress.push(task)
                else if (s === 'Completed') completed.push(task)
                else no.push(task)
            })
            return { no, inProgress, completed }
        },
        [statusFromApi]
    )

    return (
        <div className="flex-1 flex flex-col min-w-0 min-h-[420px] p-4">
                {selectedTeam ? (
                    <>
                        <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 pb-3 text-sm">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-foreground">{selectedTeam.teamName ?? '—'}</span>
                                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                    {(selectedTeam.tasks ?? []).length} {t('recoverRequests.mobilisation.tasks', 'tasks')}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/50 dark:text-green-300">
                                    {(selectedTeam.tasks ?? []).filter((tk) => statusFromApi(tk.status ?? 'No') === 'Completed').length} {t('recoverRequests.mobilisation.completed', 'Completed')}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                                    {(selectedTeam.tasks ?? []).filter((tk) => statusFromApi(tk.status ?? 'No') === 'In Progress').length} {t('recoverRequests.mobilisation.inProgress', 'In Progress')}
                                </span>
                            </div>
                            <CardTableViewToggle
                                value={viewMode}
                                onValueChange={onViewModeChange}
                                order="table-first"
                                cardLabel={t('recoverRequests.mobilisation.board', 'Board')}
                                tableLabel={t('recoverRequests.mobilisation.list', 'List')}
                                tableIcon={ListChecks}
                                iconClassName="h-4 w-4 text-primary"
                            />
                        </div>
                        <div className="flex-1 flex flex-col min-w-0 overflow-hidden p-4">
                        <div className="flex-1 grid grid-cols-3 gap-2 overflow-auto min-w-0">
                            {(() => {
                                const { no, inProgress, completed } = getTasksByStatus(selectedTeam)
                                const cols = [
                                    { key: 'no', label: statusColumns[0].label, tasks: no, dotClass: 'bg-slate-400', topBorderClass: 'border-t-slate-400', countClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
                                    { key: 'inProgress', label: statusColumns[1].label, tasks: inProgress, dotClass: 'bg-blue-500', topBorderClass: 'border-t-blue-500', countClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
                                    { key: 'completed', label: statusColumns[2].label, tasks: completed, dotClass: 'bg-green-500', topBorderClass: 'border-t-green-500', countClass: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
                                ]
                                return cols.map((col) => (
                                    <div key={col.key} className={cn('flex flex-col rounded-lg border border-border bg-slate-50 dark:bg-slate-800/30 min-w-0 border-t-4 overflow-hidden', col.topBorderClass)}>
                                        <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
                                            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                                <span className={cn('h-2 w-2 shrink-0 rounded-full', col.dotClass)} />
                                                {col.label}
                                            </span>
                                            <span className={cn('rounded-full px-2 py-0.5 font-mono text-xs font-medium', col.countClass)}>
                                                {col.tasks.length}
                                            </span>
                                        </div>
                                        <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3 min-h-[200px]">
                                            {col.tasks.length === 0 ? (
                                                <p className="py-4 text-center text-xs text-muted-foreground">{t('recoverRequests.mobilisation.noTasks', 'No tasks')}</p>
                                            ) : (
                                                col.tasks.map((task) => {
                                                    const taskId = task.recoveryMobilisationId ?? task.id
                                                    return (
                                                        <BoardTaskCard
                                                            key={taskId}
                                                            setId={setId}
                                                            task={task}
                                                            isSelected={selectedBoardCardId === taskId}
                                                            onSelect={() => onSelectBoardCard(taskId)}
                                                            getEditState={getEditState}
                                                            setTaskEdit={setTaskEdit}
                                                            handleSave={handleSave}
                                                            hasUnsavedChanges={hasUnsavedChanges}
                                                            saveMutation={saveMutation}
                                                            canEdit={canEdit}
                                                            setActionsTaskId={setActionsTaskId}
                                                            getMobilisationStatusClass={getMobilisationStatusClass}
                                                            getStatusDotClass={getStatusDotClass}
                                                            statusFromApi={statusFromApi}
                                                            t={t}
                                                        />
                                                    )
                                                })
                                            )}
                                        </div>
                                    </div>
                                ))
                            })()}
                        </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                        {t('recoverRequests.mobilisation.selectTeam', 'Select a team')}
                    </div>
                )}
        </div>
    )
}

interface Step3MobilisationTasksProps {
    teams: MobilisationTeam[]
    setId?: number
}

export function Step3MobilisationTasks({ teams, setId }: Step3MobilisationTasksProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [actionsTaskId, setActionsTaskId] = useState<number | null>(null)
    const [editState, setEditState] = useState<Record<number, { status: string; comments: string }>>({})
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState<'all' | 'active' | 'complete'>('all')
    const [viewMode, setViewMode] = useState<'card' | 'table'>('table')
    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
    const [selectedBoardCardId, setSelectedBoardCardId] = useState<number | null>(null)

    const saveMutation = useSaveTaskActionBySet(setId ?? null)
    const canEdit = setId != null && setId > 0

    const stats = useMemo(() => {
        let total = 0
        let inProgress = 0
        let completed = 0
        teams.forEach((team) => {
            const tasks = team.tasks ?? []
            tasks.forEach((tk) => {
                total++
                const s = statusFromApi(tk.status ?? '')
                if (s === 'In Progress') inProgress++
                else if (s === 'Completed') completed++
            })
        })
        return { total, inProgress, completed, teams: teams.length }
    }, [teams])

    const filteredTeams = useMemo(() => {
        let list = teams
        const q = search.trim().toLowerCase()
        if (q) list = list.filter((team) => (team.teamName ?? '').toLowerCase().includes(q))
        if (filter === 'active')
            list = list.filter((team) =>
                (team.tasks ?? []).some((tk) => statusFromApi(tk.status ?? '') === 'In Progress')
            )
        if (filter === 'complete')
            list = list.filter((team) => {
                const tasks = team.tasks ?? []
                if (tasks.length === 0) return false
                return tasks.every((tk) => statusFromApi(tk.status ?? '') === 'Completed')
            })
        return list
    }, [teams, search, filter])

    const selectedTeam = useMemo(() => {
        if (selectedTeamId == null) return filteredTeams[0] ?? null
        const team = filteredTeams.find((t) => t.id === selectedTeamId)
        return team ?? filteredTeams[0] ?? null
    }, [filteredTeams, selectedTeamId])

    useEffect(() => {
        if (filteredTeams.length === 0) return
        const inList = selectedTeamId != null && filteredTeams.some((t) => t.id === selectedTeamId)
        if (!inList) setSelectedTeamId(filteredTeams[0].id)
    }, [filteredTeams])

    const getEditState = useCallback(
        (task: MobilisationTaskRow) => {
            const id = task.recoveryMobilisationId ?? task.id
            if (editState[id]) return editState[id]
            return {
                status: statusFromApi(task.status ?? 'No'),
                comments: task.comments ?? '',
            }
        },
        [editState]
    )

    const setTaskEdit = useCallback((task: MobilisationTaskRow, field: 'status' | 'comments', value: string) => {
        const id = task.recoveryMobilisationId ?? task.id
        setEditState((prev) => ({
            ...prev,
            [id]: {
                ...(prev[id] ?? {
                    status: statusFromApi(task.status ?? 'No'),
                    comments: task.comments ?? '',
                }),
                [field]: value,
            },
        }))
    }, [])

    const handleSave = useCallback(
        (task: MobilisationTaskRow) => {
            const id = task.recoveryMobilisationId ?? task.id
            if (!setId || id <= 0) return
            const state = getEditState(task)
            saveMutation.mutate({
                recoveryMobilisationId: id,
                action: statusToApi(state.status),
                comments: state.comments || undefined,
            })
        },
        [setId, getEditState, saveMutation]
    )

    const handleStatusChange = useCallback((task: MobilisationTaskRow, newStatus: string) => {
        setTaskEdit(task, 'status', newStatus)
    }, [setTaskEdit])

    const hasUnsavedChanges = useCallback(
        (task: MobilisationTaskRow) => {
            const state = getEditState(task)
            const origStatus = statusFromApi(task.status ?? 'No')
            const origComments = task.comments ?? ''
            return state.status !== origStatus || state.comments !== origComments
        },
        [getEditState]
    )

    const handleExportReport = useCallback(() => {
        const rows: string[][] = [
            ['Team', 'Task', 'Status', 'Notes', 'Assigned'],
            ...teams.flatMap((team) =>
                (team.tasks ?? []).map((tk) => [
                    team.teamName ?? '',
                    tk.task ?? '',
                    statusFromApi(tk.status ?? ''),
                    (tk.comments ?? '').replace(/"/g, '""'),
                    tk.completedBy ?? '',
                ])
            ),
        ]
        const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\r\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `mobilisation-report-${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)
        toast({ title: t('common.exported', 'Exported'), description: 'Report downloaded.' })
    }, [teams, toast, t])

    return (
        <div className="space-y-0 -m-6">
            {/* Header - theme colours */}
            <header className="rounded-t-lg border-x border-t border-border bg-card px-6 py-5 text-foreground">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-semibold">{t('recoverRequests.mobilisation.teamMobilisation', 'Team Mobilisation')}</h1>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                            {t('recoverRequests.mobilisation.teamMobilisationSubtitle', 'Track team readiness and coordinate recovery tasks across all teams')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={handleExportReport}>
                            <Download className="mr-1.5 h-4 w-4" />
                            {t('recoverRequests.mobilisation.exportReport', 'Export Report')}
                        </Button>
                    </div>
                </div>
                {/* Stats cards - same style as Users screen: label text-sm font-medium, value text-2xl font-bold */}
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <Percent className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.totalProgress', 'Total Progress')}</p>
                                <p className="text-2xl font-bold">{stats.total ? Math.round((stats.completed / stats.total) * 100) : 0}%</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{stats.completed} of {stats.total} {t('recoverRequests.mobilisation.tasks', 'tasks')}</p>
                                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                                    <div
                                        className="h-full rounded-full bg-primary transition-all"
                                        style={{ width: `${stats.total ? (stats.completed / stats.total) * 100 : 0}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <Activity className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.inProgress', 'In Progress')}</p>
                                <p className="text-2xl font-bold">{stats.inProgress}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.activeTasks', 'active tasks')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.completed', 'Completed')}</p>
                                <p className="text-2xl font-bold">{stats.completed}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.tasksDone', 'tasks done')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.teams', 'Teams')}</p>
                                <p className="text-2xl font-bold">{stats.teams}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.mobilised', 'mobilised')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Filter bar: search and team filter only (List/Board toggle moved to team header row) */}
            <div className="flex flex-wrap items-center gap-3 border-x border-b border-border bg-muted/30 px-4 py-3">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder={t('recoverRequests.mobilisation.searchTeams', 'Search teams...')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <div className="flex rounded-md border border-border bg-background p-0.5">
                    {(['all', 'active', 'complete'] as const).map((f) => (
                        <button
                            key={f}
                            type="button"
                            onClick={() => setFilter(f)}
                            className={cn(
                                'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                                filter === f
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                        >
                            {f === 'all' && t('recoverRequests.mobilisation.allTeams', 'All Teams')}
                            {f === 'active' && t('recoverRequests.mobilisation.hasActive', 'Has Active')}
                            {f === 'complete' && t('recoverRequests.mobilisation.complete', 'Complete')}
                        </button>
                    ))}
                </div>
            </div>

            {viewMode === 'table' && (
            filteredTeams.length === 0 ? (
                <div className="rounded-b-lg border-x border-b border-border bg-card p-8 text-center text-sm text-muted-foreground">
                    {t('recoverRequests.mobilisation.noTeams', 'No teams found.')}
                </div>
            ) : (
                <div className="rounded-b-lg border-x border-b border-border bg-card flex overflow-hidden min-h-[420px]">
                    <TeamSidebar
                        filteredTeams={filteredTeams}
                        selectedTeamId={selectedTeamId}
                        onSelectTeam={setSelectedTeamId}
                        getEditState={getEditState}
                        t={t}
                    />
                    <div className="flex-1 flex flex-col min-w-0 p-4">
                        {selectedTeam ? (
                            <>
                                <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 pb-3 text-sm">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-foreground">{selectedTeam.teamName ?? '—'}</span>
                                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                            {(selectedTeam.tasks ?? []).length} {t('recoverRequests.mobilisation.tasks', 'tasks')}
                                        </span>
                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/50 dark:text-green-300">
                                            {(selectedTeam.tasks ?? []).filter((tk) => getEditState(tk).status === 'Completed').length} {t('recoverRequests.mobilisation.completed', 'Completed')}
                                        </span>
                                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                                            {(selectedTeam.tasks ?? []).filter((tk) => getEditState(tk).status === 'In Progress').length} {t('recoverRequests.mobilisation.inProgress', 'In Progress')}
                                    </span>
                                    </div>
                                    <CardTableViewToggle
                                        value={viewMode}
                                        onValueChange={setViewMode}
                                        order="table-first"
                                        cardLabel={t('recoverRequests.mobilisation.board', 'Board')}
                                        tableLabel={t('recoverRequests.mobilisation.list', 'List')}
                                        tableIcon={ListChecks}
                                        iconClassName="h-4 w-4 text-primary"
                                    />
                                </div>
                                <div className="flex-1 overflow-auto rounded-md border w-full min-w-0 overflow-hidden font-sans antialiased">
                                    <Table className="w-full min-w-full text-sm font-sans" style={{ tableLayout: 'fixed' }}>
                                            <TableHeader>
                                            <TableRow className="bg-table-header border-b border-table-border">
                                                <TableHead className="w-12 shrink-0 font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.no', 'No')}</TableHead>
                                                <TableHead className="min-w-[240px] font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.task', 'Task')}</TableHead>
                                                <TableHead className="min-w-[140px] w-[140px] shrink-0 font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.status', 'Status')}</TableHead>
                                                <TableHead className="min-w-[280px] font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.comments', 'Comments')}</TableHead>
                                                <TableHead className="min-w-[80px] w-[100px] shrink-0 font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.user', 'User')}</TableHead>
                                                {canEdit && (
                                                    <>
                                                        <TableHead className="shrink-0 text-right font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)', width: 72, minWidth: 72 }}>{t('common.actions', 'Actions')}</TableHead>
                                                        <TableHead className="w-12 shrink-0 text-right font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}></TableHead>
                                                    </>
                                                )}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                            {(selectedTeam.tasks ?? []).length === 0 ? (
                                                    <TableRow>
                                                    <TableCell colSpan={canEdit ? 7 : 5} className="text-muted-foreground text-center py-6">—</TableCell>
                                                    </TableRow>
                                                ) : (
                                                (selectedTeam.tasks ?? []).map((task, idx) => {
                                                        const state = getEditState(task)
                                                        const taskId = task.recoveryMobilisationId ?? task.id
                                                    const isSaving = saveMutation.isPending && saveMutation.variables?.recoveryMobilisationId === taskId
                                                        return (
                                                        <TableRow key={task.id} className="hover:bg-table-row-hover border-b border-table-border font-sans">
                                                            <TableCell className="font-mono align-middle text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-muted text-xs">{idx + 1}</span>
                                                            </TableCell>
                                                            <TableCell className="font-medium text-foreground text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>{task.task}</TableCell>
                                                            <TableCell className="min-w-[140px] w-[140px] shrink-0 text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                    {canEdit ? (
                                                                    <DropdownMenu>
                                                                        <DropdownMenuTrigger asChild>
                                                                            <button
                                                                                type="button"
                                                                                className={cn(
                                                                                    'inline-flex items-center gap-1.5 rounded-full border-0 px-2.5 py-0.5 text-xs font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-ring',
                                                                                    getMobilisationStatusClass(state.status)
                                                                                )}
                                                                            >
                                                                                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', getStatusDotClass(state.status))} />
                                                                                {state.status}
                                                                                <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
                                                                            </button>
                                                                        </DropdownMenuTrigger>
                                                                        <DropdownMenuContent align="start" className="min-w-[180px]">
                                                                                {STATUS_OPTIONS.map((opt) => (
                                                                                <DropdownMenuItem
                                                                                        key={opt}
                                                                                    onClick={() => handleStatusChange(task, opt)}
                                                                                    >
                                                                                    <span className={cn('mr-2 h-2 w-2 rounded-full', getStatusDotClass(opt))} />
                                                                                        {opt}
                                                                                    {state.status === opt && <Check className="ml-auto h-4 w-4" />}
                                                                                </DropdownMenuItem>
                                                                            ))}
                                                                        </DropdownMenuContent>
                                                                    </DropdownMenu>
                                                                ) : (
                                                                    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', getMobilisationStatusClass(task.status ?? 'No'))}>
                                                                        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', getStatusDotClass(task.status ?? 'No'))} />
                                                                        {statusFromApi(task.status ?? 'No')}
                                                                        </span>
                                                                    )}
                                                                </TableCell>
                                                            <TableCell className="min-w-[280px] text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                    {canEdit ? (
                                                                    <Textarea
                                                                        className="min-h-[60px] w-full min-w-0 resize-y text-sm font-sans"
                                                                            value={state.comments}
                                                                        onChange={(e) => setTaskEdit(task, 'comments', e.target.value)}
                                                                        placeholder={t('recoverRequests.mobilisation.commentsPlaceholder', 'Comments')}
                                                                        rows={2}
                                                                        />
                                                                    ) : (
                                                                    <span className="text-sm text-muted-foreground">{state.comments || '—'}</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="min-w-[80px] w-[100px] shrink-0 text-sm text-muted-foreground" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                {task.completedBy ? (
                                                                    <span className="inline-flex items-center gap-1.5">
                                                                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-medium text-foreground">
                                                                            {(task.completedBy ?? '?').slice(0, 1).toUpperCase()}
                                                                        </span>
                                                                        {task.completedBy}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted-foreground">—</span>
                                                                    )}
                                                                </TableCell>
                                                                    {canEdit && (
                                                                        <>
                                                                    <TableCell className="w-[72px] min-w-[72px] shrink-0 text-right text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)', width: 72, minWidth: 72 }}>
                                                                        {hasUnsavedChanges(task) ? (
                                                                            <Button
                                                                                size="sm"
                                                                                variant="default"
                                                                                onClick={() => handleSave(task)}
                                                                                disabled={isSaving}
                                                                            >
                                                                                {t('common.save', 'Save')}
                                                                            </Button>
                                                                        ) : (
                                                                            <span className="inline-block h-8 w-[56px]" aria-hidden />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="w-12 shrink-0 text-right text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                        <TaskActionsIconButton
                                                                            setId={setId ?? null}
                                                                            recoveryMobilisationId={taskId}
                                                                            onClick={() => setActionsTaskId(taskId)}
                                                                            title={t('recoverRequests.mobilisation.taskActions', 'Task actions')}
                                                                        />
                                                                    </TableCell>
                                                                        </>
                                                                    )}
                                                            </TableRow>
                                                        )
                                                    })
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                                {t('recoverRequests.mobilisation.selectTeam', 'Select a team')}
                                    </div>
                                )}
                    </div>
                            </div>
                        )
            )}

            {viewMode === 'card' && (
            filteredTeams.length === 0 ? (
                <div className="rounded-b-lg border-x border-b border-border bg-card p-8 text-center text-sm text-muted-foreground">
                    {t('recoverRequests.mobilisation.noTeams', 'No teams found.')}
                </div>
            ) : (
            <div className="rounded-b-lg border-x border-b border-border bg-card flex overflow-hidden min-h-[420px]">
                <TeamSidebar
                    filteredTeams={filteredTeams}
                    selectedTeamId={selectedTeamId}
                    onSelectTeam={setSelectedTeamId}
                    getEditState={getEditState}
                    t={t}
                />
                <MobilisationBoardView
                    setId={setId ?? null}
                    selectedTeam={selectedTeam}
                    selectedBoardCardId={selectedBoardCardId}
                    onSelectBoardCard={setSelectedBoardCardId}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    getEditState={getEditState}
                    setTaskEdit={setTaskEdit}
                    handleSave={handleSave}
                    hasUnsavedChanges={hasUnsavedChanges}
                    saveMutation={saveMutation}
                    canEdit={canEdit}
                    setActionsTaskId={setActionsTaskId}
                    getMobilisationStatusClass={getMobilisationStatusClass}
                    getStatusDotClass={getStatusDotClass}
                    statusFromApi={statusFromApi}
                    t={t}
                />
            </div>
            )
            )}

            {actionsTaskId != null && setId != null && (
                <TaskActionsDialog
                    setId={setId}
                    recoveryMobilisationId={actionsTaskId}
                    onClose={() => setActionsTaskId(null)}
                />
            )}
        </div>
    )
}

function getStatusPillClass(action: string): string {
    const a = (action || '').trim()
    if (a === 'Assigned') return 'bg-emerald-100 text-emerald-700'
    if (a === 'Started') return 'bg-blue-100 text-blue-700'
    if (a === 'Unassigned') return 'bg-gray-100 text-gray-700'
    if (a === 'InProgress' || a === 'In Progress') return 'bg-blue-100 text-blue-700'
    if (a === 'Completed') return 'bg-green-100 text-green-700'
    if (a === 'Issue' || a === 'Blocked') return 'bg-red-100 text-red-700'
    return 'bg-slate-100 text-slate-600'
}

function TaskActionsDialog({
    setId,
    recoveryMobilisationId,
    onClose,
}: {
    setId: number
    recoveryMobilisationId: number
    onClose: () => void
}) {
    const { t } = useTranslation()
    const { data: actions = [], isLoading } = useTaskActionsBySet(setId, recoveryMobilisationId)

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t('recoverRequests.mobilisation.taskActionsTitle', 'Task actions')}</DialogTitle>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-auto">
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground py-4">{t('common.loading', 'Loading...')}</p>
                    ) : actions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">{t('recoverRequests.mobilisation.noTaskActions', 'No actions recorded.')}</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[120px]">{t('recoverRequests.mobilisation.status', 'Status')}</TableHead>
                                    <TableHead className="min-w-[280px]">{t('recoverRequests.mobilisation.comments', 'Comments')}</TableHead>
                                    <TableHead>{t('recoverRequests.mobilisation.statusDateAndTime', 'Status date and time')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {actions.map((a) => (
                                    <TableRow key={a.id}>
                                        <TableCell className="w-[120px] align-top">
                                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusPillClass(a.action)}`}>
                                                {a.action}
                                            </span>
                                        </TableCell>
                                        <TableCell className="min-w-[280px] align-top">{a.comments || '—'}</TableCell>
                                        <TableCell className="align-top">
                                            <span className="whitespace-nowrap">
                                                {a.userName != null && a.userName !== '' && (
                                                    <>
                                                        <span className="text-muted-foreground font-medium">{a.userName}</span>
                                                        <span className="text-muted-foreground/70 mx-1.5">·</span>
                                                    </>
                                                )}
                                                {a.statusdatetime ? formatDateTime(a.statusdatetime) : '—'}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
